import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FlightRangeList from '../Components/FlightRange/FlightRangeList';
import InfoModal from '../Components/Modals/InfoModal';
import DownloadModal from '../Components/Modals/DownloadModal';
import './FlightRange.scss';

type TabType = 'flightRanges' | 'drafts' | 'history';

interface NavigationTab {
  id: TabType;
  label: string;
  path: string;
}

const navigationTabs: NavigationTab[] = [
  { id: 'flightRanges', label: 'Flight ranges', path: '/flight-ranges' },
  { id: 'drafts', label: 'Drafts', path: '/drafts' },
  { id: 'history', label: 'History', path: '/history' }
];

const FlightRangeAdmin: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);

  // Determine active tab based on current path
  const [activeTab, setActiveTab] = useState<TabType>(
    location.pathname.includes('drafts')
      ? 'drafts'
      : location.pathname.includes('history')
        ? 'history'
        : 'flightRanges'
  );

  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false);

  const handleTabChange = (tab: TabType, path: string) => {
    setActiveTab(tab);
    navigate(path);
  };

  const handleOpenInfoModal = () => {
    setIsInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalOpen(false);
    if (infoButtonRef.current) {
      infoButtonRef.current.focus();
    }
  };

  const handleOpenDownloadModal = () => {
    setIsDownloadModalOpen(true);
  };

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
    if (downloadButtonRef.current) {
      downloadButtonRef.current.focus();
    }
  };

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'flightRanges':
        return <FlightRangeList />;
      case 'drafts':
        return <div>Drafts content</div>;
      case 'history':
        return <div>History content</div>;
      default:
        return <FlightRangeList />;
    }
  };

  return (
      <div className="flight-range" data-testid="flight-range-admin">
        <div className="flight-range__header">
          {/* Navigation Tabs */}
          <nav
            className="flight-range__tabs"
            role="navigation"
            aria-label="Flight range sections"
          >
            {navigationTabs.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                className={`flight-range__tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id, tab.path)}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.label}
              </Link>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flight-range__actions">
            <button
              ref={infoButtonRef}
              className="flight-range__info-button"
              onClick={handleOpenInfoModal}
              aria-label="Show information"
              aria-haspopup="dialog"
              type="button"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
                  fill="#0074CA"
                  fillOpacity="0.1"
                />
                <path
                  d="M8 3.5C8.41421 3.5 8.75 3.83579 8.75 4.25C8.75 4.66421 8.41421 5 8 5C7.58579 5 7.25 4.66421 7.25 4.25C7.25 3.83579 7.58579 3.5 8 3.5Z"
                  fill="#0074CA"
                />
                <path
                  d="M8.75 12V7H7.25V12H8.75Z"
                  fill="#0074CA"
                />
              </svg>
            </button>
            <button
              ref={downloadButtonRef}
              className="flight-range__download-button"
              onClick={handleOpenDownloadModal}
              aria-label="Open download options"
              aria-haspopup="dialog"
              type="button"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M14 5H10V1H6V5H2L8 11L14 5ZM2 13V15H14V13H2Z"
                  fill="#0074CA"
                />
              </svg>
              <span>Download xls</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flight-range__content">
          {renderContent()}
        </div>

        {/* Modals */}
        <InfoModal
          isOpen={isInfoModalOpen}
          onClose={handleCloseInfoModal}
          title="Info"
          content={
            <>
              <p className="info-modal__description">
                This tool displays flight ranges, un-published drafts, and a history of emails that outline flight range changes.
              </p>
              <div className="info-modal__section">
                <h3>Activity</h3>
                <p>Future activity for flight ranges are shown in red or green.</p>
                <p>
                  <span className="text-success">Effective date</span> = Date the flight will be operational (first flight date).
                </p>
                <p>
                  <span className="text-danger">Discontinued date</span> = Last date the flight will be operational.
                </p>
              </div>
              <div className="info-modal__section">
                <h3>Discontinued flights</h3>
                <p>Flights that are discontinued cannot be modified more than once.</p>
                <p>Any flights discontinued before today's date are not shown.</p>
              </div>
            </>
          }
        />

        <DownloadModal
          isOpen={isDownloadModalOpen}
          onClose={handleCloseDownloadModal}
          activeTab={activeTab}
        />
      </div>
  );
};

export default FlightRangeAdmin;